import { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Select, MenuItem, Chip } from '@mui/material';
import { Text, Button } from '@qwickapps/react-framework';
import { config } from '../../config/runtime';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  namespace: string;
  message: string;
}

const levelColors: Record<string, string> = {
  debug: '#9e9e9e',
  info: '#2196f3',
  warn: '#ff9800',
  error: '#f44336',
};

type LevelFilter = 'all' | 'debug' | 'info' | 'warn' | 'error';

export default function LogsPanel() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all');
  const [fetchError, setFetchError] = useState(false);

  const fetchLogs = useCallback(async (level: LevelFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200', order: 'desc' });
      if (level !== 'all') params.set('level', level);
      const res = await fetch(`${config.apiUrl}/api/logs?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const entries: LogEntry[] = Array.isArray(data) ? data : (data.logs ?? []);
      setLogs(entries);
      setFetchError(false);
    } catch {
      setFetchError(true);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(levelFilter);
  }, [fetchLogs, levelFilter]);

  const handleRefresh = () => {
    fetchLogs(levelFilter);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Text variant="h5" content="Logs" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LevelFilter)}
            size="small"
            sx={{ minWidth: 120, bgcolor: 'var(--theme-surface)' }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="debug">Debug</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="warn">Warn</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
          <Button onClick={handleRefresh}>Refresh</Button>
        </Box>
      </Box>

      <Text
        variant="body2"
        content="Service log stream from forge-service portal"
        customColor="var(--theme-text-secondary)"
        gutterBottom
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : fetchError ? (
        <Box
          sx={{
            p: 4,
            borderRadius: 2,
            bgcolor: 'var(--theme-surface)',
            border: '1px solid var(--theme-border)',
            textAlign: 'center',
          }}
        >
          <Text
            variant="body1"
            content="Could not load logs"
            customColor="var(--theme-text-secondary)"
          />
          <Text
            variant="caption"
            content="Check that the forge-service portal is reachable"
            customColor="var(--theme-text-disabled)"
          />
        </Box>
      ) : logs.length === 0 ? (
        <Box
          sx={{
            p: 4,
            borderRadius: 2,
            bgcolor: 'var(--theme-surface)',
            border: '1px solid var(--theme-border)',
            textAlign: 'center',
          }}
        >
          <Text
            variant="body1"
            content="No logs found"
            customColor="var(--theme-text-secondary)"
          />
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid var(--theme-border)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
            {logs.map((entry, index) => (
              <LogRow key={entry.id || index} entry={entry} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function LogRow({ entry }: { entry: LogEntry }) {
  const color = levelColors[entry.level] ?? '#9e9e9e';
  const formattedTime = new Date(entry.timestamp).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        px: 2,
        py: 1,
        borderBottom: '1px solid var(--theme-border)',
        bgcolor: 'var(--theme-surface)',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { bgcolor: 'var(--theme-surface-hover)' },
      }}
    >
      <Text
        variant="caption"
        content={formattedTime}
        customColor="var(--theme-text-disabled)"
        sx={{ whiteSpace: 'nowrap', minWidth: 80 }}
      />
      <Chip
        label={entry.level}
        size="small"
        sx={{
          bgcolor: color,
          color: 'white',
          fontSize: 10,
          height: 20,
          minWidth: 48,
          textTransform: 'uppercase',
        }}
      />
      <Text
        variant="caption"
        content={entry.namespace}
        customColor="var(--theme-text-secondary)"
        sx={{ minWidth: 120, whiteSpace: 'nowrap' }}
      />
      <Text variant="body2" content={entry.message} sx={{ flex: 1, wordBreak: 'break-all' }} />
    </Box>
  );
}
